import { useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { useForm, FormProvider } from 'react-hook-form';
import * as yup from 'yup';
import { SplitModal as Modal, PreviewWrapper } from '@components/SplitModal';
import {
    CONTACT_FIELDS,
    hasRequiredIdentifyingField,
    pickContactFields,
    useContactFieldsValues,
} from '@features/ContactFieldsSettings';
import { LOCATION_TYPE } from '@features/ComboLocationSelect';
import { useTranslation, useYupValidationResolver } from '@hooks';
import { VIEW_TYPE, PREVIEW_LOCALES } from '@constants/microsites';
import { FORM_INTERNAL_NAME_MAX_LENGTH } from '@constants/forms';
import Settings from './Settings';
import Preview from './Preview';
import { areTextsComplete, buildFormDefaults, toFormModel } from '../helpers';

const FormWrapper = styled('div')({
    padding: '24px 16px',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    '@media (max-width: 768px)': {
        overflowY: 'initial',
        overflowX: 'initial',
    },
});

const FormsSchema = yup.object().shape({
    internalName: yup
        .string()
        .required('validation.required')
        .max(FORM_INTERNAL_NAME_MAX_LENGTH, 'validation.maxLength'),
    locationType: yup.string(),
    locationId: yup.string().when('locationType', {
        is: LOCATION_TYPE.LOCATION,
        then: schema => schema.required('validation.required'),
        otherwise: schema => schema.nullable(),
    }),
    brandId: yup.string().when('locationType', {
        is: LOCATION_TYPE.BRAND,
        then: schema => schema.required('validation.required'),
        otherwise: schema => schema.nullable(),
    }),
});

const EditModal = ({ modal, onClose, selectedForm, onDelete, onSave, account, isUser, brands }) => {
    const { t } = useTranslation();
    const resolver = useYupValidationResolver(FormsSchema);
    const currentLocale = PREVIEW_LOCALES[account?.locale];

    const {
        setValue,
        control,
        watch,
        handleSubmit,
        getValues,
        formState: { isValid, isDirty, isSubmitting, errors },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: buildFormDefaults({
            language: currentLocale,
            confirmationMessage: t('FormsPage.defaultConfirmationMessage'),
        }),
    });

    const texts = watch('texts');
    const contactFields = useContactFieldsValues(CONTACT_FIELDS, control);
    const textsComplete = useMemo(() => areTextsComplete(texts), [texts]);
    const identityValid = hasRequiredIdentifyingField(contactFields, CONTACT_FIELDS);

    useEffect(() => {
        if (!selectedForm) return;
        setValue('internalName', selectedForm.internalName, { shouldValidate: true });
        setValue('texts', selectedForm.texts?.length ? selectedForm.texts : getValues('texts'));
        setValue('labels', selectedForm.labels || []);
        Object.entries(pickContactFields(selectedForm)).forEach(([name, value]) => setValue(name, value));
    }, [selectedForm, setValue, getValues]);

    const isView = modal.type === VIEW_TYPE.VIEW;
    const isCreate = modal.type === VIEW_TYPE.CREATE;

    const submit = async values => {
        await onSave(toFormModel(selectedForm, values));
    };

    return (
        <Modal
            fullScreen
            isOpen={modal.open}
            handleClose={onClose}
            isDirty={isDirty}
            title={t('FormsPage.modalTitle')}
            onPrimaryAction={isUser ? null : handleSubmit(submit)}
            primaryActionText={t('buttons.save')}
            primaryActionDisabled={
                isSubmitting || !isValid || (!isCreate && !isDirty) || !textsComplete || !identityValid
            }
            onAdditionalAction={isCreate || isUser ? null : onDelete}
            additionalTextButton={t('FormsPage.deleteForm')}
        >
            <FormProvider {...{ setValue, control, isValid, watch, getValues, errors }}>
                <FormWrapper>
                    <Settings
                        isView={isView}
                        selectedForm={selectedForm}
                        brands={brands}
                        currentLocale={currentLocale}
                    />
                </FormWrapper>
                <PreviewWrapper>
                    <Preview formId={selectedForm?.id} />
                </PreviewWrapper>
            </FormProvider>
        </Modal>
    );
};

export default EditModal;
